/**
 * Real backend adapter for the Document Hub (epic FD-3570), wired to the
 * confirmed BE contract (reference + PRs #4144 / #4153, 2026-07-06):
 *
 *   presign  POST /file-upload/presign   { files:[{fileName,type,contentType}] }
 *   upload   PUT  <signedUrl>            (raw bytes)
 *   persist  POST /documents            { documents:[{companyId?,roundId?,fileName,fileSize,contentType,s3Key}] }
 *   list     GET  /documents            -> { items, total }
 *   get      GET  /documents/:id        -> CompanyDocument | 404
 *   reprocess POST /documents/:id/reprocess -> CompanyDocument (reset to detecting; CS-only)
 *   update   PATCH /documents/:id       -> CompanyDocument (confirm + override)
 *   download GET  /documents/:id/download -> { downloadUrl } (presigned; FD-3744)
 *   delete   DELETE /documents/:id       -> 204, or 409 when the state forbids it
 *
 * The only backend path (no mock). Some endpoints may still be deploying
 * (e.g. `GET /documents`), so callers handle empty/error states. Boundary notes:
 *   - `fileSize` is a STRING in responses → parsed to a number here.
 *   - `companyName`/`roundLabel` come straight from the response if present,
 *     else null; `companyName` is resolved client-side from `companyId`
 *     (see useCompanyNames) since the docs API doesn't return the name.
 *   - Detection runs server-side (FD-3689): documents advance
 *     `uploaded → detecting → detected`; the UI polls `GET /documents` while any
 *     row is still in-flight (see useDocuments).
 */
import axios from 'axios';

import { MaggieHttp } from '../../services/MaggieHttp';
import { getFileExtension, isZipFile } from '../../util/file-utils';
import { CONTENT_TYPE_BY_EXTENSION } from './documentHub.constants';
import { CompanyDocument, DocumentExtraction, RawCompanyDocument } from './documentHub.schema';
import {
  AddToPackageInput,
  CreatePackageInput,
  DocumentDownloadUrlResponse,
  UpdateDocumentInput,
  UploadAttribution,
} from './documentHub.types';
import type { DocumentHubApi } from './documentHub.api';

/** S3 key prefix / presign `type` for Document Hub uploads (per BE contract). */
const DOCUMENT_HUB_TYPE = 'document-hub';

interface PresignUpload {
  type: string;
  signedUrl: string;
  fileId: string;
  s3Key: string;
  fileName: string;
}
interface PresignResponse {
  success: boolean;
  uploads: PresignUpload[];
}

interface ListDocumentsResponse {
  items: RawCompanyDocument[];
  total: number;
}

function contentTypeFor(file: File): string {
  if (file.type) {
    return file.type;
  }
  const ext = getFileExtension(file.name);
  return CONTENT_TYPE_BY_EXTENSION[ext] ?? 'application/octet-stream';
}

/** Normalize the wire shape into the FE model (parse fileSize; names resolved later). */
function toCompanyDocument(raw: RawCompanyDocument): CompanyDocument {
  return {
    id: raw.id,
    companyId: raw.companyId,
    companyName: raw.companyName ?? raw.companyLabel ?? null,
    fileName: raw.fileName,
    fileSize: Number(raw.fileSize),
    contentType: raw.contentType,
    s3Key: raw.s3Key,
    docType: raw.docType,
    period: raw.period,
    roundId: raw.roundId,
    roundLabel: raw.roundLabel ?? null,
    roundDate: raw.roundDate ?? null,
    eventType: raw.eventType ?? null,
    dealDocumentType: raw.dealDocumentType ?? null,
    packageId: raw.packageId ?? null,
    // Default enrolled=true until the BE ships the flag, so rollout can't flash a false warning.
    confirmSyncedAt: raw.confirmSyncedAt ?? null,
    companyEnrolled: raw.companyEnrolled ?? true,
    detectionError: raw.detectionError ?? null,
    status: raw.status,
    clientStatus: raw.clientStatus,
    confidence: raw.confidence,
    uploadedBy: raw.uploadedBy,
    emailMetadata: raw.emailMetadata ?? null,
    createdAt: raw.createdAt,
  };
}

// Upload batch id counter (the BE has no batch concept; batchId is only used
// FE-side to key per-file upload progress in the Uploading modal).
let batchCounter = 0;

/** The list endpoint's own `@Max` on `limit`; `offset` is unbounded, so depth comes from paging. */
const DOCUMENTS_PAGE_SIZE = 200;

/**
 * Runaway guard, not a product limit: page count is driven by the server's `total`,
 * so this only trips if that count is wrong. Set far above any real document count.
 */
const MAX_DOCUMENT_PAGES = 50;

async function fetchDocumentsPage(offset: number, companyId?: number): Promise<ListDocumentsResponse> {
  const { data } = await MaggieHttp.maggie.get<ListDocumentsResponse>('/documents', {
    // `companyId` scopes the list to one company's documents (BE WHERE clause);
    // omitted entirely when unset so the list returns all documents.
    params: { limit: DOCUMENTS_PAGE_SIZE, offset, ...(companyId != null ? { companyId } : {}) },
  });
  return data;
}

/**
 * Every document, not the newest page of them. The review queue, its counts and the
 * table's status filter are all derived client-side from this list, so a partial one
 * silently under-reports work rather than looking truncated (FD-5314).
 *
 * Pages after the first are fetched together: it is one round trip instead of N, and
 * the narrower window makes a mid-fetch insert shifting rows between pages less likely.
 */
async function listDocuments(companyId?: number): Promise<CompanyDocument[]> {
  const firstPage = await fetchDocumentsPage(0, companyId);
  const pageCount = Math.min(Math.ceil(firstPage.total / DOCUMENTS_PAGE_SIZE), MAX_DOCUMENT_PAGES);
  const laterPages = await Promise.all(
    Array.from({ length: Math.max(pageCount - 1, 0) }, (_, i) =>
      fetchDocumentsPage((i + 1) * DOCUMENTS_PAGE_SIZE, companyId)
    )
  );
  // Rows are ordered newest-first, so a document uploaded mid-fetch pushes later pages
  // back and can repeat a row across two of them. Keying by id drops the repeat and
  // keeps first-seen order, which is the server's ordering.
  const byId = new Map<number, RawCompanyDocument>();
  for (const page of [firstPage, ...laterPages]) {
    for (const item of page.items) {
      byId.set(item.id, item);
    }
  }
  return Array.from(byId.values(), toCompanyDocument);
}

async function getDocument(id: number): Promise<CompanyDocument> {
  const { data } = await MaggieHttp.maggie.get<RawCompanyDocument>(`/documents/${id}`);
  return toCompanyDocument(data);
}

async function reprocessDocument(id: number): Promise<CompanyDocument> {
  const { data } = await MaggieHttp.maggie.post<undefined, RawCompanyDocument>(
    `/documents/${id}/reprocess`,
    undefined
  );
  return toCompanyDocument(data);
}

/**
 * The BE retires the document in Starbreaker, withdraws any values it produced and
 * soft-deletes the row. Nothing is returned — the caller refetches the list.
 */
async function deleteDocument(id: number): Promise<void> {
  await MaggieHttp.maggie.delete<void>(`/documents/${id}`);
}

async function createPackage(input: CreatePackageInput): Promise<CompanyDocument[]> {
  const { data } = await MaggieHttp.maggie.post<CreatePackageInput, RawCompanyDocument[]>(
    '/documents/packages',
    input
  );
  return data.map(toCompanyDocument);
}

async function addToPackage({ documentId, packageId }: AddToPackageInput): Promise<CompanyDocument> {
  const { data } = await MaggieHttp.maggie.post<{ packageId: string }, RawCompanyDocument>(
    `/documents/${documentId}/package`,
    { packageId }
  );
  return toCompanyDocument(data);
}

async function removeFromPackage(documentId: number): Promise<CompanyDocument> {
  const { data } = await MaggieHttp.maggie.delete<RawCompanyDocument>(`/documents/${documentId}/package`);
  return toCompanyDocument(data);
}

async function updateDocument(id: number, patch: UpdateDocumentInput): Promise<CompanyDocument> {
  const { data } = await MaggieHttp.maggie.patch<UpdateDocumentInput, RawCompanyDocument>(
    `/documents/${id}`,
    patch
  );
  return toCompanyDocument(data);
}

/**
 * GET /documents/:id/download — a short-lived presigned S3 GET URL for the
 * original file. The BE sets content-disposition from the file name, so the FE
 * can hand the URL straight to a download anchor.
 */
async function getDownloadUrl(id: number): Promise<DocumentDownloadUrlResponse> {
  const { data } = await MaggieHttp.maggie.get<DocumentDownloadUrlResponse>(`/documents/${id}/download`);
  return data;
}

/**
 * POST /documents/extract — fan an already-uploaded ZIP (identified by s3Key)
 * into per-file documents server-side. Returns 202 immediately; extraction runs
 * in the background and children surface via GET /documents. `signal` lets a
 * cancel abort the request (extraction already accepted server-side is not
 * cancellable, but this keeps the batch abort consistent).
 */
async function postExtractZip(
  s3Key: string,
  attribution: UploadAttribution,
  signal?: AbortSignal
): Promise<{ accepted: boolean }> {
  const { data } = await MaggieHttp.maggie.post<UploadAttribution & { s3Key: string }, { accepted: boolean }>(
    '/documents/extract',
    { ...attribution, s3Key },
    { signal }
  );
  return { accepted: Boolean(data?.accepted) };
}

/**
 * GET /documents/extractions?s3Key= — poll a ZIP fan-out's status after the 202.
 * Returns null on 404 (the status record isn't there yet, or the endpoint isn't
 * deployed), so the caller can keep polling quietly instead of erroring out.
 */
async function getExtraction(s3Key: string): Promise<DocumentExtraction | null> {
  try {
    const { data } = await MaggieHttp.maggie.get<DocumentExtraction>('/documents/extractions', {
      params: { s3Key },
      // 404 = the status row doesn't exist yet (created only after ZIP validation passes),
      // so it's expected on every poll until then — don't report it to Sentry (matches
      // the AtlasQueries "not deployed / no record" pattern).
      skipSentryStatuses: [404],
    });
    return data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      return null;
    }
    throw err;
  }
}

export const realDocumentHubApi: DocumentHubApi = {
  uploadBatch(files, handlers = {}, attribution: UploadAttribution = {}) {
    const batchId = `batch-${(batchCounter += 1)}`;
    const clientIds = files.map((_file, i) => `${batchId}-file-${i}`);

    const done = (async () => {
      const { data: presign } = await MaggieHttp.maggie.post<
        { files: { fileName: string; type: string; contentType: string }[] },
        PresignResponse
      >('/file-upload/presign', {
        files: files.map((file) => ({
          fileName: file.name,
          type: DOCUMENT_HUB_TYPE,
          contentType: contentTypeFor(file),
        })),
      });

      // Upload each file's bytes to its presigned S3 URL. A loose file is marked
      // `uploaded` here; a ZIP is only marked uploaded after its extract call is
      // accepted (below) so a failed extract surfaces as an upload error rather
      // than a premature transition to review.
      for (let i = 0; i < files.length; i += 1) {
        if (handlers.signal?.aborted) {
          throw new DOMException('Upload aborted', 'AbortError');
        }
        const upload = presign.uploads[i];
        try {
          await axios.put(upload.signedUrl, files[i], {
            headers: { 'Content-Type': contentTypeFor(files[i]) },
            signal: handlers.signal,
            onUploadProgress: (e) => {
              const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
              handlers.onItemProgress?.(clientIds[i], pct);
            },
          });
        } catch (err) {
          if (axios.isCancel(err) || handlers.signal?.aborted) {
            throw new DOMException('Upload aborted', 'AbortError');
          }
          throw new Error(`S3 upload failed for ${files[i].name}`);
        }
        handlers.onItemProgress?.(clientIds[i], 100);
        if (!isZipFile(files[i])) {
          handlers.onItemDone?.(clientIds[i]);
        }
      }

      if (handlers.signal?.aborted) {
        throw new DOMException('Upload aborted', 'AbortError');
      }

      // Persist loose (non-ZIP) files as documents (idempotent on s3Key). A ZIP is
      // never persisted — it is fanned out server-side into child documents.
      const looseIndexes = files.map((_file, i) => i).filter((i) => !isZipFile(files[i]));
      let documents: CompanyDocument[] = [];
      if (looseIndexes.length > 0) {
        const { data: created } = await MaggieHttp.maggie.post<
          {
            documents: (UploadAttribution & {
              fileName: string;
              fileSize: number;
              contentType: string;
              s3Key: string;
            })[];
          },
          RawCompanyDocument[]
        >(
          '/documents',
          {
            documents: looseIndexes.map((i) => ({
              ...attribution,
              fileName: presign.uploads[i].fileName,
              fileSize: files[i].size,
              contentType: contentTypeFor(files[i]),
              s3Key: presign.uploads[i].s3Key,
            })),
          },
          { signal: handlers.signal }
        );
        documents = created.map(toCompanyDocument);
      }

      // Trigger extraction for each ZIP; mark its item `uploaded` once accepted.
      const extractedZipKeys: string[] = [];
      for (let i = 0; i < files.length; i += 1) {
        if (!isZipFile(files[i])) {
          continue;
        }
        if (handlers.signal?.aborted) {
          throw new DOMException('Upload aborted', 'AbortError');
        }
        await postExtractZip(presign.uploads[i].s3Key, attribution, handlers.signal);
        extractedZipKeys.push(presign.uploads[i].s3Key);
        handlers.onItemDone?.(clientIds[i]);
      }

      return { documents, extractedZipKeys };
    })();

    return { start: { batchId, clientIds }, done };
  },
  listDocuments,
  getDocument,
  updateDocument,
  createPackage,
  addToPackage,
  removeFromPackage,
  reprocessDocument,
  deleteDocument,
  getDownloadUrl,
  getExtraction,
};
